import type { ReactNode } from 'react';

/**
 * Строка списка в админке — первый из трёх примитивов.
 *
 * Девять вкладок это не девять экранов. Рисовать каждую отдельно, да ещё
 * дважды под веб и телефон, значит завести восемнадцать вёрсток, которые
 * разъедутся к третьей правке. Это уже происходило: чипы фильтров на
 * четырёх вкладках выглядели по-разному.
 *
 * Поэтому вкладка описывается **списком колонок**, а не собственной
 * вёрсткой. На широком экране колонки, на телефоне те же поля в том же
 * порядке сверху вниз — человек, привыкший к вебу, узнаёт телефон и не
 * переучивается.
 *
 * Главное правило: **в строке ровно одно действие** — то, ради которого
 * на вкладку пришли. Всё остальное в разборе (`AdminSheet`). Три кнопки
 * на карточку и сделали нынешний экран тесным: три строки помещалось
 * там, где помещается одиннадцать.
 */

export interface Column<T> {
  /** Заголовок колонки на широком экране. */
  title: string;
  /** Ширина колонки: любое значение для `grid-template-columns`. */
  width?: string;
  /** Что показать. Одна и та же функция на оба размера. */
  cell: (row: T) => ReactNode;
  /**
   * Показывать ли подпись колонки на телефоне.
   *
   * У «Участника» подпись не нужна — имя говорит само за себя; у
   * «Заходил» нужна, иначе дата висит без объяснения.
   */
  labelOnPhone?: boolean;
  /** Не показывать на телефоне вовсе: столбец служебный. */
  webOnly?: boolean;
  /**
   * На телефоне встать в общую строку с соседями, а не занять свою.
   *
   * Для коротких значений — счётчиков, рейтинга, даты. Шесть полей по
   * строке каждое дают карточку в двести пикселей: на экран влезает
   * две, и список перестаёт быть списком.
   */
  inline?: boolean;
}

interface Props<T> {
  columns: Column<T>[];
  rows: T[];
  keyOf: (row: T) => string;
  /** Единственное действие строки. Подпись одна на все строки. */
  action?: { label: string; onClick: (row: T) => void };
  /** Подсветить строку целиком: «ждёт допуска» ищут глазами по списку. */
  highlight?: (row: T) => boolean;
  empty?: string;
}

export default function AdminList<T>({
  columns,
  rows,
  keyOf,
  action,
  highlight,
  empty = 'Ничего не найдено',
}: Props<T>) {
  if (rows.length === 0) return <div className="empty">{empty}</div>;

  // Последняя колонка — под действие: оно всегда справа и всегда на
  // одном месте, чтобы его не приходилось искать в каждой строке заново.
  const template =
    columns.map((c) => c.width ?? 'minmax(0, 1fr)').join(' ') + (action ? ' 116px' : '');

  return (
    <div className="alist">
      <div className="alist__head" style={{ gridTemplateColumns: template }}>
        {columns.map((c) => (
          <div key={c.title}>{c.title}</div>
        ))}
        {action && <div />}
      </div>

      {rows.map((row) => (
        /*
         * Нажимается вся строка, а не только кнопка.
         *
         * Кнопка остаётся: она называет действие и даёт крупную цель
         * пальцу. Но целиться в неё, когда открыть нужно строку целиком,
         * — лишняя точность: человек уже ведёт курсор к нужной фамилии,
         * и требовать доехать до правого края незачем.
         *
         * Роль и обработка клавиш обязательны: без них строка нажимается
         * мышью и не нажимается ничем другим.
         */
        <div
          key={keyOf(row)}
          className={
            (highlight?.(row) ? 'alist__row alist__row--mark' : 'alist__row') +
            (action ? ' alist__row--tap' : '')
          }
          style={{ gridTemplateColumns: template }}
          role={action ? 'button' : undefined}
          tabIndex={action ? 0 : undefined}
          onClick={action ? () => action.onClick(row) : undefined}
          onKeyDown={
            action
              ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    action.onClick(row);
                  }
                }
              : undefined
          }
        >
          {columns.map((c) => (
            <div
              key={c.title}
              className={
                'alist__cell' +
                (c.webOnly ? ' alist__cell--web' : '') +
                (c.inline ? ' alist__cell--inline' : '')
              }
            >
              {/* Подпись поля видна только на телефоне: в таблице её роль
                  играет шапка, и дублировать её в каждой ячейке значит
                  повторить заголовок столько раз, сколько строк. */}
              {c.labelOnPhone && <span className="alist__label">{c.title}</span>}
              <span className="alist__value">{c.cell(row)}</span>
            </div>
          ))}

          {action && (
            <div className="alist__act">
              <button
                type="button"
                className="secondary alist__go"
                // Нажатие не всплывает до строки: иначе один щелчок по
                // кнопке считался бы двумя.
                onClick={(e) => {
                  e.stopPropagation();
                  action.onClick(row);
                }}
              >
                {action.label}
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
